import React from 'react';
import { Box, Typography } from '@mui/material';

import { useChatBot, useGetConfig } from './ChatBot';
import { trackWidgetEvent } from '../utils/analytics';
import { useIsMobileWidget } from '../utils/device';
import { useEffectiveQuickStartPlacement } from '../hooks/useEffectiveQuickStartPlacement';
import { LauncherTeaserBubble } from './LauncherTeaserBubble';
import { FloatingButtonHoverChips } from './FloatingButtonHoverChips';
import { ChatFloatingButtonFab } from './ChatFloatingButtonFab';
import { useChatFloatingButtonTeaser } from './useChatFloatingButtonTeaser';
import { canShowWhatsAppDeflection } from '@/lib/whatsapp-utils';
import { useLauncherEmbedStyle } from '../context/LauncherEmbedStyleContext';
import { visuallyHiddenSx } from './visuallyHiddenSx';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';

export function ChatFloatingButton() {
  const config = useGetConfig();
  const { t, locale } = useWidgetI18n();
  const { mx, my, zIndex } = useLauncherEmbedStyle();
  const { toggleChatUi, open, launcherRef, history, send, hasSentMessageInSession } =
    useChatBot();
  const isMobile = useIsMobileWidget();
  const placement = useEffectiveQuickStartPlacement();

  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);

  const { teaserMode, showTeaser, teaserMessage, fabPulseSx, handleTeaserDismiss, dismissTeaserOnOpen } =
    useChatFloatingButtonTeaser(config);

  if (open) return null;

  const showLabel =
    !isMobile && (config.widgetLauncherStyle ?? 'icon_with_name') === 'icon_with_name';

  const buttons = config.quickStartButtons ?? [];
  const hasUserInitiated = history.some((msg) => msg.role === 'user');
  const hideForFrequency =
    config.quickStartFrequency === 'until_first_message' &&
    Boolean(hasSentMessageInSession);
  const showHoverChips =
    placement === 'on_hover' &&
    buttons.length > 0 &&
    !hasUserInitiated &&
    !hideForFrequency;
  const chipsVisible = showHoverChips && (hovered || focused);

  const whatsappDeflectionVisible = canShowWhatsAppDeflection(
    config.whatsappPhoneNumber,
    config.whatsappDeflectionEnabled,
  );

  const openChat = () => {
    trackWidgetEvent('Chat Widget - Chat widget opened to side panel', {
      teaserMode,
      whatsappDeflectionVisible,
    });
    toggleChatUi(true);
  };

  const handleFabClick = () => {
    if (showTeaser) {
      dismissTeaserOnOpen();
    }
    openChat();
  };

  const handleChipClick = (label: string, message: string) => {
    if (showTeaser) {
      dismissTeaserOnOpen();
    }
    trackWidgetEvent('Chat Widget - Collapsed Quick Start Button Clicked', {
      buttonLabel: label,
      teaserMode,
    });
    send(message);
    toggleChatUi(true);
  };

  return (
    <Box
      role="complementary"
      aria-labelledby="launcher-landmark-label"
      sx={{
        position: 'fixed',
        bottom: `${my}px`,
        right: `${mx}px`,
        zIndex,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={(e) => {
        const el = e.target as HTMLElement;
        if (el.matches?.(':focus-visible')) setFocused(true);
      }}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setFocused(false);
        }
      }}
    >
      <Typography
        component="h2"
        id="launcher-landmark-label"
        lang={locale}
        sx={visuallyHiddenSx}
      >
        {t('launcher.chatAssistant')}
      </Typography>
      {showTeaser && config.launcherTeaserStyle === 'message' && (
        <LauncherTeaserBubble
          message={teaserMessage}
          onDismiss={handleTeaserDismiss}
          onOpenChat={handleFabClick}
        />
      )}

      {showHoverChips && (
        <FloatingButtonHoverChips
          buttons={buttons}
          chipsVisible={chipsVisible}
          onChipClick={handleChipClick}
        />
      )}

      <ChatFloatingButtonFab
        showLabel={showLabel}
        agentName={config.agentName}
        chatButtonAccent={config.chatButtonAccent ?? 'primary_only'}
        fabPulseSx={fabPulseSx}
        launcherRef={launcherRef}
        onClick={handleFabClick}
      />
    </Box>
  );
}
